import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { deleteMember, fetchMembers, MemberRow, updateMember } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";

const GENDER_OPTIONS = ["Male", "Female", "Other"];
const NATIONALITY_OPTIONS = ["India", "Other"];
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const MemberList: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [error, setError] = useState("");
  const [viewRow, setViewRow] = useState<MemberRow | null>(null);
  const [editRow, setEditRow] = useState<MemberRow | null>(null);
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [stateSearchValue, setStateSearchValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [resumeDragOver, setResumeDragOver] = useState(false);
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);

  useEffect(() => {
    fetchMembers()
      .then(setRows)
      .catch((err: Error) => setError(err.message || "Failed to fetch members"));
  }, []);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const states = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => (r.state || "").trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const searchMatch =
        !q ||
        r.fullName?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.memberId?.toLowerCase().includes(q);
      const stateMatch = stateFilter === "all" || (r.state || "").toLowerCase() === stateFilter.toLowerCase();
      const genderMatch = genderFilter === "all" || (r.gender || "").toLowerCase() === genderFilter.toLowerCase();
      return searchMatch && stateMatch && genderMatch;
    });
  }, [rows, search, stateFilter, genderFilter]);

  const getInitials = (name: string) =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("");

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this member?")) return;
    try {
      await deleteMember(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Delete failed");
    }
  };

  const onSave = async () => {
    if (!editRow) return;
    const hasValidState = stateOptions.some(
      (option) => option.toLowerCase() === (editRow.state || "").trim().toLowerCase()
    );
    if (!hasValidState) {
      alert("Please select a valid state from the dropdown.");
      return;
    }
    setSaving(true);
    try {
      await updateMember(editRow);
      setRows((prev) => prev.map((item) => (item.id === editRow.id ? { ...item, ...editRow } : item)));
      setEditRow(null);
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const toMediaUrl = (value?: string) => {
    if (!value) return "";
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    return `${API_URL}${value}`;
  };

  const formatDisplayDate = (value?: string) => {
    if (!value) return "-";
    const raw = String(value).trim();

    // Keep explicit day-month-year dates untouched.
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      return raw;
    }

    // Plain date values (YYYY-MM-DD): map directly, no timezone conversion.
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.slice(0, 10).split("-");
      return `${day}-${month}-${year}`;
    }

    // Timestamp values: render in IST to avoid one-day drift from UTC conversion.
    if (raw.includes("T")) {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear());
    return `${day}-${month}-${year}`;
  };

  const toInputDate = (value?: string) => {
    if (!value) return "";
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split("-");
      return `${year}-${month}-${day}`;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const normalizedGenderValue = (value?: string) => {
    const raw = (value || "").trim();
    if (!raw) return "";
    const found = GENDER_OPTIONS.find((option) => option.toLowerCase() === raw.toLowerCase());
    return found || raw;
  };

  const normalizedNationalityValue = (value?: string) => {
    const raw = (value || "").trim();
    if (!raw) return "";
    const found = NATIONALITY_OPTIONS.find((option) => option.toLowerCase() === raw.toLowerCase());
    return found || raw;
  };

  const stateOptions = useMemo(() => {
    const merged = [...INDIAN_STATES, ...states, (editRow?.state || "").trim()].filter(Boolean);
    return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
  }, [states, editRow?.state]);

  const filteredStateOptions = useMemo(() => {
    const q = stateSearchValue.trim().toLowerCase();
    if (!q) return stateOptions;
    return stateOptions.filter((option) => option.toLowerCase().includes(q));
  }, [stateOptions, stateSearchValue]);

  return (
    <DashboardLayout>
      <ActionToast action={toastAction} />
      <h1 className="list-title">Members</h1>
      <p className="list-subtitle">{rows.length} total members</p>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="list-toolbar list-toolbar-members">
        <div className="search-wrap">
          <i className="fa-solid fa-magnifying-glass search-icon" />
          <input
            className="form-control search-input"
            placeholder="Search by name, email, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="form-select toolbar-select" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
          <option value="all">All States</option>
          {states.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
        <select className="form-select toolbar-select" value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="table-shell table-responsive">
        <table className="table data-table mb-0">
          <thead>
            <tr>
              <th style={{ width: 70 }} />
              <th>ID</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>City/State</th>
              <th>Joined</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id}>
                <td>
                  <span className="table-avatar">{getInitials(row.fullName || "")}</span>
                </td>
                <td>{row.memberId || row.id}</td>
                <td>{row.fullName}</td>
                <td>{row.mobile}</td>
                <td>{row.email}</td>
                <td>
                  {row.city}, {row.state}
                </td>
                <td>{row.createdAt || "-"}</td>
                <td className="text-end">
                  <div className="action-icons">
                    <button type="button" className="icon-btn" title="View Details" onClick={() => setViewRow(row)}>
                      <i className="fa-regular fa-eye" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Edit"
                      onClick={() => {
                        setEditRow({ ...row });
                        setStateSearchValue(row.state || "");
                        setShowStateOptions(false);
                      }}
                    >
                      <i className="fa-regular fa-pen-to-square" />
                    </button>
                    <button type="button" className="icon-btn icon-btn-danger" title="Delete" onClick={() => onDelete(row.id)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-4 text-muted">
                  No members found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {viewRow && (
        <div className="dash-modal-overlay" onClick={() => setViewRow(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 className="dash-modal-title">Member Details</h3>
              <button type="button" className="dash-modal-close" onClick={() => setViewRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">Full Name</div>
                  <div className="detail-value">{viewRow.fullName || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Mobile</div>
                  <div className="detail-value">{viewRow.mobile || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{viewRow.email || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Aadhar</div>
                  <div className="detail-value">{viewRow.aadhar || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Reference By</div>
                  <div className="detail-value">{viewRow.referenceBy || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Gender</div>
                  <div className="detail-value">{viewRow.gender || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Date of Birth</div>
                  <div className="detail-value">{formatDisplayDate(viewRow.dateOfBirth)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Father's Name</div>
                  <div className="detail-value">{viewRow.fathersName || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">State</div>
                  <div className="detail-value">{viewRow.state || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">City</div>
                  <div className="detail-value">{viewRow.city || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Pin Code</div>
                  <div className="detail-value">{viewRow.pinCode || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Address</div>
                  <div className="detail-value">{viewRow.address || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Nationality</div>
                  <div className="detail-value">{viewRow.nationality || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Qualification</div>
                  <div className="detail-value">{viewRow.qualification || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Area of Operation</div>
                  <div className="detail-value">{viewRow.areaOfOperation || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Experience</div>
                  <div className="detail-value">{viewRow.experience || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Brief About Yourself</div>
                  <div className="detail-value">{viewRow.briefAboutYourself || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Resume</div>
                  <div className="detail-value">
                    {viewRow.resumeUrl ? (
                      <a href={toMediaUrl(viewRow.resumeUrl)} target="_blank" rel="noreferrer">
                        View Resume
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Photo</div>
                  <div className="detail-value">
                    {viewRow.photoUrl ? (
                      <img
                        src={toMediaUrl(viewRow.photoUrl)}
                        alt={viewRow.fullName || "Member"}
                        style={{ width: "96px", height: "96px", objectFit: "cover", borderRadius: "10px", border: "1px solid #dfe5ef" }}
                      />
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setViewRow(null)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setEditRow({ ...viewRow });
                  setStateSearchValue(viewRow.state || "");
                  setShowStateOptions(false);
                  setViewRow(null);
                }}
              >
                Edit Details
              </button>
            </div>
          </div>
        </div>
      )}

      {editRow && (
        <div className="dash-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 className="dash-modal-title">Edit Member</h3>
              <button type="button" className="dash-modal-close" onClick={() => setEditRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div className="edit-grid">
                <div className="edit-field">
                  <label className="edit-label">Full Name</label>
                  <input className="form-control" placeholder="Enter full name" value={editRow.fullName || ""} onChange={(e) => setEditRow({ ...editRow, fullName: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Mobile</label>
                  <input className="form-control" placeholder="Enter mobile number" value={editRow.mobile || ""} onChange={(e) => setEditRow({ ...editRow, mobile: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Email</label>
                  <input className="form-control" placeholder="Enter email address" value={editRow.email || ""} onChange={(e) => setEditRow({ ...editRow, email: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Aadhar</label>
                  <input className="form-control" placeholder="Enter Aadhar number" value={editRow.aadhar || ""} onChange={(e) => setEditRow({ ...editRow, aadhar: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Reference By</label>
                  <input className="form-control" placeholder="Enter reference by" value={editRow.referenceBy || ""} onChange={(e) => setEditRow({ ...editRow, referenceBy: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Gender</label>
                  <select className="form-select" value={normalizedGenderValue(editRow.gender)} onChange={(e) => setEditRow({ ...editRow, gender: e.target.value })}>
                    <option value="">Select Gender</option>
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizedGenderValue(editRow.gender) && !GENDER_OPTIONS.includes(normalizedGenderValue(editRow.gender)) && (
                      <option value={normalizedGenderValue(editRow.gender)}>{normalizedGenderValue(editRow.gender)}</option>
                    )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Date of Birth</label>
                  <input className="form-control" type="date" placeholder="Select date of birth" title="Select date of birth" value={toInputDate(editRow.dateOfBirth)} onChange={(e) => setEditRow({ ...editRow, dateOfBirth: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Father's Name</label>
                  <input className="form-control" placeholder="Enter father's name" value={editRow.fathersName || ""} onChange={(e) => setEditRow({ ...editRow, fathersName: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">State</label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="form-control"
                      placeholder="Search or select state"
                      value={stateSearchValue}
                      onFocus={() => setShowStateOptions(true)}
                      onChange={(e) => {
                        setStateSearchValue(e.target.value);
                        setShowStateOptions(true);
                      }}
                      onBlur={() => {
                        const matched = stateOptions.find(
                          (option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase()
                        );
                        if (matched) {
                          setStateSearchValue(matched);
                          setEditRow({ ...editRow, state: matched });
                        } else {
                          setStateSearchValue(editRow.state || "");
                        }
                        setTimeout(() => setShowStateOptions(false), 120);
                      }}
                    />
                    {showStateOptions && filteredStateOptions.length > 0 && (
                      <div
                        className="list-group"
                        style={{
                          position: "absolute",
                          zIndex: 20,
                          width: "100%",
                          maxHeight: "180px",
                          overflowY: "auto",
                          border: "1px solid #dfe5ef",
                          borderRadius: "8px",
                          marginTop: "6px",
                          background: "#fff",
                        }}
                      >
                        {filteredStateOptions.slice(0, 50).map((option) => (
                          <button
                            key={option}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setStateSearchValue(option);
                              setEditRow({ ...editRow, state: option });
                              setShowStateOptions(false);
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="edit-field">
                  <label className="edit-label">City</label>
                  <input className="form-control" placeholder="Enter city" value={editRow.city || ""} onChange={(e) => setEditRow({ ...editRow, city: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Pin Code</label>
                  <input className="form-control" placeholder="Enter pin code" value={editRow.pinCode || ""} onChange={(e) => setEditRow({ ...editRow, pinCode: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Address</label>
                  <input className="form-control" placeholder="Enter full address" value={editRow.address || ""} onChange={(e) => setEditRow({ ...editRow, address: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Nationality</label>
                  <select className="form-select" value={normalizedNationalityValue(editRow.nationality)} onChange={(e) => setEditRow({ ...editRow, nationality: e.target.value })}>
                    {NATIONALITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizedNationalityValue(editRow.nationality) &&
                      !NATIONALITY_OPTIONS.includes(normalizedNationalityValue(editRow.nationality)) && (
                        <option value={normalizedNationalityValue(editRow.nationality)}>{normalizedNationalityValue(editRow.nationality)}</option>
                      )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Qualification</label>
                  <input className="form-control" placeholder="Enter qualification" value={editRow.qualification || ""} onChange={(e) => setEditRow({ ...editRow, qualification: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Area of Operation</label>
                  <input className="form-control" placeholder="Enter area of operation" value={editRow.areaOfOperation || ""} onChange={(e) => setEditRow({ ...editRow, areaOfOperation: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Experience</label>
                  <input className="form-control" placeholder="Enter experience" value={editRow.experience || ""} onChange={(e) => setEditRow({ ...editRow, experience: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Brief About Yourself</label>
                  <textarea className="form-control" rows={3} placeholder="Write brief about yourself" value={editRow.briefAboutYourself || ""} onChange={(e) => setEditRow({ ...editRow, briefAboutYourself: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Resume Upload</label>
                  <label
                    className={`dash-upload-box ${resumeDragOver ? "is-dragover" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setResumeDragOver(true);
                    }}
                    onDragLeave={() => setResumeDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setResumeDragOver(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) setEditRow({ ...editRow, resumeFile: file });
                    }}
                  >
                    <i className="fa-regular fa-file-lines" />
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      className="dash-upload-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setEditRow({ ...editRow, resumeFile: file });
                      }}
                    />
                  </label>
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Photo Upload</label>
                  <label
                    className={`dash-upload-box ${photoDragOver ? "is-dragover" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setPhotoDragOver(true);
                    }}
                    onDragLeave={() => setPhotoDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setPhotoDragOver(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) setEditRow({ ...editRow, photoFile: file });
                    }}
                  >
                    <i className="fa-regular fa-image" />
                    <input
                      type="file"
                      accept="image/*"
                      className="dash-upload-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setEditRow({ ...editRow, photoFile: file });
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={onSave}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default MemberList;
